import { supabase } from '../supabaseClient';

export class CaseService {
  // CASES PAGINATION AND FILTERS
  static async getCasesPaged({
    page = 1,
    pageSize = 20,
    status,
    assignedTo,
    dateFrom,
    dateTo,
    search,
    clientId,
    sortBy = 'created_at', // created_at | updated_at | due_date | priority | status | assigned_to
    sortDir = 'desc'
  } = {}) {
    try {
      const from = Math.max(0, (page - 1) * pageSize);
      const to = from + pageSize - 1;
      let query = supabase
        .from('cases')
        .select('*', { count: 'exact' });

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }
      if (assignedTo && assignedTo.trim()) {
        query = query.ilike('assigned_to', `%${assignedTo.trim()}%`);
      }
      if (dateFrom) {
        query = query.gte('created_at', dateFrom);
      }
      if (dateTo) {
        query = query.lte('created_at', dateTo);
      }
      if (search && search.trim()) {
        const term = `%${search.trim()}%`;
        query = query.or(`title.ilike.${term},priority.ilike.${term},current_stage.ilike.${term},assigned_to.ilike.${term}`);
      }

      const allowedSort = new Set(['created_at', 'updated_at', 'due_date', 'priority', 'status', 'assigned_to']);
      const sortField = allowedSort.has(sortBy) ? sortBy : 'created_at';
      query = query.order(sortField, { ascending: String(sortDir).toLowerCase() === 'asc' });

      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: data || [], count: count || 0 };
    } catch (error) {
      console.error('Error fetching paginated cases:', error);
      return { rows: [], count: 0 };
    }
  }

  static async updateCase(id, updates) {
    try {
      const { data, error } = await supabase
        .from('cases')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating case:', error);
      throw error;
    }
  }

  static async bulkUpdateCases(ids = [], updates = {}) {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    try {
      const { data, error } = await supabase
        .from('cases')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .in('id', ids)
        .select();
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error bulk updating cases:', error);
      throw error;
    }
  }

  // UNIFIED DEADLINES & CONTROL ROOM SUPPORT
  static async getUnifiedDeadlines(filters = {}) {
    try {
      const {
        startDate = new Date().toISOString().split('T')[0],
        endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        alertLevels = ['critical', 'urgent', 'attention'],
        deadlineTypes = ['task', 'university_application', 'visa_application', 'agreement', 'case'],
        assignee = null,
        clientId = null,
        caseId = null,
        includeAcknowledged = false
      } = filters;

      // Prefer RPC (for server-side filtering), but fall back to view when RPC is unavailable/ambiguous
      const rpcPayload = {
        start_date: startDate,
        end_date: endDate,
        alert_levels: alertLevels,
        deadline_types: deadlineTypes,
        assignee,
        client_id: clientId,
        case_id: caseId,
        p_client_id: clientId, // compatibility with older signature
        p_case_id: caseId,
        include_acknowledged: !!includeAcknowledged
      };

      const { data, error } = await supabase.rpc('get_unified_deadlines', rpcPayload);
      let rows = data || [];

      if (error || !Array.isArray(rows)) {
        console.warn('RPC get_unified_deadlines failed, falling back to unified_deadlines view', error);

        let query = supabase
          .from('unified_deadlines')
          .select('*')
          .gte('deadline', startDate)
          .lte('deadline', endDate);

        if (Array.isArray(deadlineTypes) && deadlineTypes.length > 0) {
          query = query.in('deadline_type', deadlineTypes);
        }
        if (assignee) query = query.eq('assigned_to', assignee);
        if (clientId) query = query.eq('client_id', clientId);
        if (caseId) query = query.eq('case_id', caseId);

        const { data: viewData, error: viewError } = await query;
        if (viewError) throw viewError;

        // Mirror RPC-calculated fields
        rows = (viewData || []).map((row) => {
          const deadline = row.deadline ? new Date(row.deadline) : null;
          const hoursUntil = deadline ? (deadline.getTime() - Date.now()) / (1000 * 60 * 60) : Number.POSITIVE_INFINITY;

          let alertLevel = null;
          if (hoursUntil <= 24) alertLevel = 'critical';
          else if (hoursUntil <= 72) alertLevel = 'urgent';
          else if (hoursUntil <= 168) alertLevel = 'attention';

          const urgencyScore =
            hoursUntil <= 24 ? 100 :
              hoursUntil <= 72 ? 75 :
                hoursUntil <= 168 ? 50 :
                  hoursUntil <= 336 ? 25 : 10;

          return {
            ...row,
            alert_level: alertLevel,
            urgency_score: urgencyScore
          };
        }).filter((row) => {
          return !alertLevels || alertLevels.length === 0 || alertLevels.includes(row.alert_level);
        });
      }

      return {
        data: rows,
        error: null,
        metadata: {
          total: rows.length,
          filters,
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error fetching unified deadlines:', error);
      return { data: [], error: error.message, metadata: {} };
    }
  }

  static async getDeadlineStats(dateRangeDays = 7) {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - dateRangeDays * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];

      const { data: rows, error } = await CaseService.getUnifiedDeadlines({
        startDate,
        endDate,
        alertLevels: ['critical', 'urgent', 'attention'],
        deadlineTypes: ['task', 'university_application', 'visa_application', 'agreement']
      });

      if (error) throw new Error(error);

      const safeRows = rows || [];
      const stats = {
        total: safeRows.length,
        critical: safeRows.filter((d) => d.alert_level === 'critical').length,
        urgent: safeRows.filter((d) => d.alert_level === 'urgent').length,
        attention: safeRows.filter((d) => d.alert_level === 'attention').length,
        overdue: safeRows.filter((d) => d.overdue).length,
        dueToday:
          safeRows.filter((d) => {
            if (!d.deadline) return false;
            const deadline = new Date(d.deadline);
            const today = new Date();
            return deadline.toDateString() === today.toDateString();
          }).length || 0
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching deadline stats:', error);
      return { data: {}, error: error.message };
    }
  }

  static async getPendingTasks(filters = {}) {
    try {
      const {
        assignee = null,
        caseSearch = null,
        status = null,
        dueStart = null,
        dueEnd = null
      } = filters;

      let query = supabase
        .from('case_tasks')
        .select(`
          id,
          title,
          status,
          due_date,
          assigned_to,
          stage_id,
          case_id,
          cases:case_id (
            id,
            title,
            client_id
          )
        `)
        .neq('status', 'completed');

      if (assignee) query = query.ilike('assigned_to', `%${assignee}%`);
      if (status) query = query.eq('status', status);
      if (dueStart) query = query.gte('due_date', dueStart);
      if (dueEnd) query = query.lte('due_date', dueEnd);
      if (caseSearch) query = query.ilike('cases.title', `%${caseSearch}%`);

      const { data, error } = await query;
      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
      return { data: [], error: error.message };
    }
  }

  static async acknowledgeDeadlineAlert(deadlineType, entityId, metadata = {}) {
    try {
      const { data, error } = await supabase
        .from('deadline_alerts')
        .insert({
          deadline_type: deadlineType,
          entity_id: entityId,
          alert_level: 'attention',
          acknowledged_at: new Date().toISOString(),
          metadata
        })
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error acknowledging deadline alert:', error);
      return { data: null, error: error.message };
    }
  }

  static async bulkUpdateDeadlines(updates) {
    try {
      if (!Array.isArray(updates) || updates.length === 0) {
        return { data: { successful: [], errors: [] }, error: null };
      }

      const results = await Promise.all(
        updates.map(async (update) => {
          const { entityId, entityType, field, value, existingMetadata } = update;

          let query;
          switch (entityType) {
            case 'task':
              query = supabase.from('case_tasks').update({ [field]: value }).eq('id', entityId);
              break;
            case 'university_application':
              query = supabase
                .from('uni_applications')
                .update({
                  metadata: {
                    ...(existingMetadata || {}),
                    [field]: value
                  }
                })
                .eq('id', entityId);
              break;
            case 'visa_application':
              query = supabase.from('visa_applications').update({ [field]: value }).eq('id', entityId);
              break;
            case 'agreement':
              query = supabase.from('agreements').update({ [field]: value }).eq('id', entityId);
              break;
            default:
              return {
                data: null,
                error: `Unsupported entity type: ${entityType}`,
                entityId,
                entityType
              };
          }

          const { data, error } = await query.select();
          return { data, error, entityId, entityType };
        })
      );

      const errors = results.filter((r) => r.error);
      const successful = results.filter((r) => !r.error);

      return {
        data: { successful, errors },
        error: errors.length ? `${errors.length} updates failed` : null
      };
    } catch (error) {
      console.error('Error in bulk deadline updates:', error);
      return { data: { successful: [], errors: [] }, error: error.message };
    }
  }
}
